/**
 * BrightSignBridge — JavaScript implementation of the TrillboardsNativeDevice
 * contract for BrightSign players.
 *
 * This is the JavaScript-side counterpart to the Android Kotlin bridge
 * (trillboard-ctv/agent-core-lite/.../NativeDeviceBridge.kt). The React
 * screen code uses `window.TrillboardsNativeDevice` without knowing which
 * platform it's running on; this adapter fulfils the contract on BrightSign.
 *
 * Key differences from the Android bridge:
 *  - BrightSign has no advertising ID (no GMS / IFA / AAID pipeline), so
 *    advertisingId / advertisingIdType / limitAdTracking are always null.
 *  - FPS telemetry is forwarded to the host BrightScript shell via
 *    BSMessagePort.PostBSMessage({ type: 'fps', fps }).
 *  - Device identity is read from the synchronous window.BSDeviceInfo
 *    globals that the autorun.brs shell injects into the HTML zone before
 *    the React bundle loads.
 *
 * Bridge protocol version: 1 (matches the Android bridge).
 */

const BRIDGE_VERSION = 1;

/**
 * Normalize BSDeviceInfo.model into a series tier used by the server-side
 * capability profile. BrightSign model numbers follow a predictable pattern
 * where the series prefix encodes the tier (LS/HD/XD/XT/XC).
 *
 * Examples:
 *   LS445 → 'ls'
 *   HD224 → 'hd'
 *   XD235 → 'xd'
 *   XT1145 → 'xt'
 *   XC2055 → 'xc'
 */
function deriveModelTier(model) {
  if (!model || typeof model !== 'string') return null;
  const upper = model.toUpperCase();
  if (upper.startsWith('LS')) return 'ls';
  if (upper.startsWith('HD')) return 'hd';
  if (upper.startsWith('XD')) return 'xd';
  if (upper.startsWith('XT')) return 'xt';
  if (upper.startsWith('XC')) return 'xc';
  return null;
}

/**
 * Build the device identity payload from the BSDeviceInfo globals. This
 * mirrors the JSON shape emitted by the Kotlin bridge's getDeviceInfo().
 *
 * BSDeviceInfo is injected by autorun.brs via BSMessagePort before the
 * HTML zone loads, so it is synchronously available when the bridge is
 * installed.
 */
function buildDeviceInfoPayload() {
  const bs = (typeof window !== 'undefined' && window.BSDeviceInfo) || {};
  const model = typeof bs.model === 'string' ? bs.model : 'BrightSign';
  const firmware = typeof bs.firmwareVersion === 'string' ? bs.firmwareVersion : 'unknown';
  const modelTier = deriveModelTier(model);

  return {
    make: 'BrightSign',
    model,
    os: 'BrightSign OS',
    osVersion: firmware,
    // apiLevel is an Android concept — BrightSign has no API level, so we
    // report 0 to keep the field typed consistently with the Android bridge.
    apiLevel: 0,
    deviceType: 'dooh',
    bridgeVersion: BRIDGE_VERSION,
    // BrightSign has no advertising ID; honour the Android contract by
    // leaving these null so server-side code can branch on their absence.
    advertisingId: null,
    advertisingIdType: null,
    limitAdTracking: null,
    // BrightSign-specific extras — additive fields are safe because the
    // Android contract does not forbid extra keys. Server + screen code
    // that only reads the core fields will ignore these.
    serialNumber: typeof bs.serialNumber === 'string' ? bs.serialNumber : null,
    firmwareVersion: firmware,
    deviceFamily: typeof bs.deviceFamily === 'string' ? bs.deviceFamily : null,
    bootVersion: typeof bs.bootVersion === 'string' ? bs.bootVersion : null,
    modelTier,
  };
}

/**
 * Safely clamp and coerce an FPS value to a finite number before forwarding
 * to the BrightScript shell. The React rAF counter can emit non-finite
 * values during suspend/resume; dropping them prevents BSMessagePort from
 * receiving garbage payloads.
 */
function coerceFps(fps) {
  const n = Number(fps);
  if (!Number.isFinite(n)) return null;
  if (n < 0) return 0;
  if (n > 240) return 240; // sanity cap — BrightSign HDMI outputs max at 120 Hz
  return n;
}

/**
 * Implementation of window.TrillboardsNativeDevice for BrightSign. Methods
 * mirror the @JavascriptInterface-annotated methods on NativeDeviceBridge.kt.
 *
 * All methods are synchronous and safe to call from the React bundle before
 * any rendering has happened — the bootstrap installs this before the
 * React entry point runs.
 */
const BrightSignBridge = {
  /**
   * Returns a JSON string with ground-truth device identity. Mirrors
   * NativeDeviceBridge.kt getDeviceInfo().
   */
  getDeviceInfo() {
    const payload = buildDeviceInfoPayload();
    try {
      return JSON.stringify(payload);
    } catch (err) {
      // JSON.stringify can only fail on circular refs, which shouldn't
      // happen here, but fall back to a minimal envelope rather than
      // throwing into the bridge caller.
      return JSON.stringify({
        make: 'BrightSign',
        model: 'BrightSign',
        os: 'BrightSign OS',
        osVersion: 'unknown',
        apiLevel: 0,
        deviceType: 'dooh',
        bridgeVersion: BRIDGE_VERSION,
        advertisingId: null,
        advertisingIdType: null,
        limitAdTracking: null,
      });
    }
  },

  /**
   * Returns the bridge protocol version. Callers use this for forward
   * compatibility — if we add new methods in bridge v2 the screen code
   * can branch on this value.
   */
  getBridgeVersion() {
    return BRIDGE_VERSION;
  },

  /**
   * Forwards a WebView FPS sample to the BrightScript host via
   * BSMessagePort. Matches the Android contract where the JS SDK reports
   * rAF-derived FPS every few seconds so native code can emit telemetry.
   */
  reportFps(fps) {
    const clamped = coerceFps(fps);
    if (clamped === null) return;
    try {
      if (
        typeof window !== 'undefined' &&
        window.BSMessagePort &&
        typeof window.BSMessagePort.PostBSMessage === 'function'
      ) {
        // BrightSign BSMessagePort.PostBSMessage accepts a string payload;
        // we stringify here so the BrightScript side can use formatJson().
        window.BSMessagePort.PostBSMessage(
          JSON.stringify({ type: 'fps', fps: clamped })
        );
      }
    } catch (err) {
      // Message-port errors must never break the React runloop — the
      // Android bridge is fire-and-forget and so is this.
    }
  },
};

/**
 * Install the bridge at window.TrillboardsNativeDevice. This is called
 * from bootstrap.js after the BSDeviceInfo / BSMessagePort globals have
 * been verified. Exported separately so tests can invoke it in isolation.
 */
function installBrightSignBridge() {
  if (typeof window === 'undefined') return null;
  window.TrillboardsNativeDevice = BrightSignBridge;
  return BrightSignBridge;
}

export {
  BrightSignBridge,
  installBrightSignBridge,
  buildDeviceInfoPayload,
  deriveModelTier,
  coerceFps,
  BRIDGE_VERSION,
};
